import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { providerAPI } from '../lib/api';
import {
  Building2, DollarSign, TrendingUp, Users, CheckCircle,
  Mail, Phone, Link as LinkIcon, FileText, ArrowRight, Star,
  Compass, ChevronLeft, Loader2
} from 'lucide-react';

export default function JoinTeamPage() {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    tour_info: '',
    tour_links: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.tour_info) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Submit application via backend API
      const response = await providerAPI.submitApplication(formData);
      
      if (response.data?.success) {
        setSubmitted(true);
        toast.success('Application submitted successfully!');
      } else {
        throw new Error(response.data?.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="font-heading text-4xl font-bold mb-4">Application Submitted!</h1>
            <p className="text-xl text-muted-foreground mb-8">
              Thank you for your interest in partnering with Tour Finder Puerto Rico. 
              We've received your application and will review it shortly.
            </p>
            <Card className="p-6 mb-8 text-left">
              <h3 className="font-semibold mb-3">What happens next?</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Our team will review your tour information within 24-48 hours</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>We'll contact you to discuss partnership details and commission rates</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Once approved, your tours will be listed on our platform</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>You'll receive a provider dashboard to track bookings and earnings</span>
                </li>
              </ul>
            </Card>
            <div className="flex justify-center gap-4">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <ChevronLeft className="h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/tours">
                <Button className="gap-2">
                  Browse Tours
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background">
      {/* Header */}
      <div className="bg-primary text-white py-16">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6">
            <ChevronLeft className="h-4 w-4" />
            Back to Tour Finder
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Partner with Tour Finder
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Join our network of tour operators and reach thousands of travelers visiting Puerto Rico. 
            No upfront costs - commission-based partnership.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Benefits Section */}
          <div>
            <h2 className="font-heading text-2xl font-bold mb-6">Why Partner With Us?</h2>
            
            <div className="space-y-6">
              <Card className="p-5">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">No Upfront Costs</h3>
                    <p className="text-muted-foreground">
                      Zero listing fees or monthly charges. We only earn when you earn - 
                      pure commission-based partnership.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-5">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Reach More Customers</h3>
                    <p className="text-muted-foreground">
                      Get exposure to thousands of travelers actively searching for 
                      Puerto Rico tours and experiences.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-5">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Track Your Success</h3>
                    <p className="text-muted-foreground">
                      Access your provider dashboard to monitor bookings, track earnings, 
                      and receive monthly profit reports.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-5">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Build Your Reputation</h3>
                    <p className="text-muted-foreground">
                      Collect reviews and ratings from happy customers to boost 
                      your visibility and credibility.
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Commission Info */}
            <Card className="p-6 mt-8 bg-primary/5 border-primary/20">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                How Commission Works
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Commission rate: Typically 15-20% per booking</li>
                <li>• You set your tour prices - we add our commission on top</li>
                <li>• Monthly payouts directly to your account</li>
                <li>• Transparent reporting - see every transaction</li>
                <li>• No hidden fees or charges</li>
              </ul>
            </Card>
          </div>

          {/* Application Form */}
          <div>
            <Card className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold">Apply to Join</h2>
                  <p className="text-sm text-muted-foreground">Fill out the form below to get started</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Your Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <Label>Company/Business Name</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      placeholder="Adventure Tours Inc."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email Address *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                  <div>
                    <Label>Phone Number *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+1 (787) 555-1234"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Tell Us About Your Tours *</Label>
                  <Textarea
                    value={formData.tour_info}
                    onChange={(e) => setFormData({...formData, tour_info: e.target.value})}
                    placeholder="Describe the tours you offer, locations, duration, what makes them unique..."
                    rows={5}
                    required
                  />
                </div>

                <div>
                  <Label>Links to Your Tours (Website, Social Media, etc.)</Label>
                  <Textarea
                    value={formData.tour_links}
                    onChange={(e) => setFormData({...formData, tour_links: e.target.value})}
                    placeholder="https://yourwebsite.com&#10;https://instagram.com/yourtours&#10;https://facebook.com/yourtours"
                    rows={3}
                  />
                </div>

                <Button type="submit" className="w-full gap-2" size="lg" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4" />
                      Submit Application
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  By submitting, you agree to our terms and conditions. 
                  We'll review your application and contact you within 24-48 hours.
                </p>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
